/**
 * Workflow Face Verification — main component.
 *
 * Replicates FaceVerification.tsx exactly. The only differences are:
 *  - Token comes from IDME_INIT postMessage (WE already created the iProov session),
 *    not from a backend session creation call.
 *  - On iProov `passed`: submits _input to WE then polls until terminal,
 *    instead of polling the IVA verification endpoint.
 *  - No consent logic (no IVA session token).
 *
 * States match FaceVerification.tsx exactly:
 *   initializing → ready → capturing → processing → completed | error
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useConsumer } from "../ConsumerProvider";
import { submitWorkflowInput, getWorkflowExecution } from "../../shared/api/workflowEngine";
import { useWorkflowBiometricConsent } from "../../hooks/faceVerification/useWorkflowBiometricConsent";
import type { WorkflowExecutionResponse } from "../../shared/types/workflowEngine";
import { useWorkflowIProovEvents } from "./useWorkflowIProovEvents";
import { WorkflowIProovSlots } from "./WorkflowIProovSlots";
import { Footer } from "../faceVerification/Footer";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { getIProovLanguage } from "../../locales/iproov";
import { loadIProovWebSdk } from "../../shared/api/vendors/iproov";
import { logger } from "../../shared/utils/logger";
import styles from "../faceVerification/FaceVerification.module.scss";

export type WorkflowFaceVerificationState =
  | "initializing"
  | "ready"
  | "capturing"
  | "processing"
  | "completed"
  | "error";

interface InitPayload {
  iproovToken: string;
  iproovBaseUrl: string;
  executionId: string;
  workflowId: number;
  inspectionName?: string;
  authToken?: string;
  apiBaseUrl?: string;
}

const DEFAULT_API_BASE = "https://enroll.idme.test/api/workflow";
const DEFAULT_INSPECTION_NAME = "face_verification";

function getBiometricConsentApiBaseUrl(apiBaseUrl?: string): string | null {
  if (!apiBaseUrl) return null;
  try {
    return `${new URL(apiBaseUrl).origin}/api/biometric-consent`;
  } catch {
    return null;
  }
}

function normalizeIProovBaseUrl(baseUrl: string): string {
  if (baseUrl.startsWith("wss://") || baseUrl.startsWith("ws://")) {
    const wsUrl = new URL(baseUrl);
    return `https://${wsUrl.host}`;
  }
  return baseUrl;
}

export function WorkflowFaceVerification() {
  const { t, i18n } = useTranslation();
  const { fragnoLogoEnabled } = useConsumer();
  const iproovRef = useRef<HTMLElement>(null);

  const [state, setState] = useState<WorkflowFaceVerificationState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ percent: 0, message: "" });
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const initDataRef = useRef<InitPayload | null>(null);
  const parentOriginRef = useRef<string>("*");
  const { definitionUuid, fetchConsent } = useWorkflowBiometricConsent();

  // Validate parent origin from document.referrer on mount
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[WorkflowFaceVerification] Parent origin validated:", origin);
      } else {
        logger.warn("[WorkflowFaceVerification] Parent origin not in allowlist:", origin);
      }
    } catch {
      logger.warn("[WorkflowFaceVerification] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => {
    initDataRef.current = initData;
  }, [initData]);

  // ── Error helper ──────────────────────────────────────────────────────
  const handleVerificationError = useCallback((errorCode: string, message: string) => {
    setError(message);
    setState("error");
    logger.error(`[WorkflowFaceVerification] Error [${errorCode}]:`, message);
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: { errorCode, message, executionId: initDataRef.current?.executionId },
      },
      parentOriginRef.current
    );
  }, []);

  // Starts the download early; the IDME_INIT handler awaits it and reports a failure.
  useEffect(() => {
    void loadIProovWebSdk().catch(() => {});
  }, []);

  // ── Consent handlers (mirrors FaceVerification.tsx) ───────────────────────
  const handleAcceptBiometricConsent = useCallback(async () => {
    const data = initDataRef.current;
    const biometricConsentApiBaseUrl = getBiometricConsentApiBaseUrl(data?.apiBaseUrl);
    if (!biometricConsentApiBaseUrl || !definitionUuid) {
      throw new Error('Missing apiBaseUrl or definitionUuid for biometric consent');
    }
    logger.log("[WorkflowFaceVerification] Posting biometric consent...");
    const response = await fetch(`${biometricConsentApiBaseUrl}/v1/consents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ definitionUuid }),
      credentials: 'include',
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const apiMessage = errorBody?.errors?.[0]?.message || errorBody?.detail || null;
      const err = new Error(apiMessage ?? `Consent API failed: ${response.status}`);
      (err as any).apiMessage = apiMessage;
      (err as any).httpStatus = response.status;
      throw err;
    }
    logger.log("[WorkflowFaceVerification] Biometric consent recorded");
  }, [definitionUuid]);

  const handleBiometricConsentFailed = useCallback((error: Error) => {
    const isTimeout = error.message === 'Consent request timed out';
    const message = isTimeout ? 'Consent API timed out' : ((error as any).apiMessage ?? 'Consent API request failed');
    logger.error("[WorkflowFaceVerification] Consent POST failed, cancelling iProov:", error.message);
    // Cancel iProov and surface the error in the IPA — fires VERIFICATION_ERROR to parent.
    handleVerificationError("consent_post_api_failed", message);
  }, [handleVerificationError]);

  const handleBiometricConsentGetFailed = useCallback((err: Error) => {
    logger.error("[WorkflowFaceVerification] Consent GET failed:", err.message);
    handleVerificationError("consent_get_api_failed", (err as any).apiMessage ?? 'Consent Get API failed');
  }, [handleVerificationError]);

  // ── Post-iProov flow: submit _input → poll WE until terminal ──────────
  const handleVerificationPassed = useCallback(async () => {
    const data = initDataRef.current;
    if (!data) return;

    setState("processing");

    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;
    const inspectionName = data.inspectionName || DEFAULT_INSPECTION_NAME;

    try {
      // POST selfie_capture_complete to WE — uses the workflowId URL so WE can
      // route the input to the correct workflow instance.
      logger.log("[WorkflowFaceVerification] Submitting selfie_capture_complete to WE");
      await submitWorkflowInput(
        apiBase,
        data.workflowId,
        data.executionId,
        {
          inspectionName,
          input: {
            inputType: "selfie_capture_complete",
            selfie_capture_complete: true,
            captureOutcome: "completed",
          },
        },
        data.authToken
      );

      // Poll until WE reaches a terminal state (COMPLETED / FAILED / CANCELLED)
      // OR until the face_verification inspection itself shows SUCCESS.
      // In combined workflows (Ial2IdentityProofing), face_enroll runs server-side
      // after face_verification — the execution may stay RUNNING while that happens,
      // so we treat face_verification.status === "SUCCESS" as our done signal.
      logger.log("[WorkflowFaceVerification] Polling for terminal state");
      const exec = await (async (): Promise<WorkflowExecutionResponse> => {
        const intervalMs = 2000;
        const deadline = Date.now() + 120_000;
        while (Date.now() < deadline) {
          const current = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);
          if (current.status === "COMPLETED" || current.status === "FAILED" || current.status === "CANCELLED") {
            return current;
          }
          const fvStatus = (current.inspections as Record<string, { status: string }> | undefined)?.[inspectionName]?.status;
          if (fvStatus === "SUCCESS") {
            logger.log("[WorkflowFaceVerification] face_verification inspection SUCCESS — treating as complete");
            return current;
          }
          logger.log("[WorkflowFaceVerification] Polling, status:", current.status, "fv inspection:", fvStatus ?? "none");
          await new Promise((r) => setTimeout(r, intervalMs));
        }
        throw new Error("Polling timed out waiting for face verification to complete");
      })();

      logger.log("[WorkflowFaceVerification] Terminal state:", exec.status);

      if (exec.status === "FAILED" || exec.status === "CANCELLED") {
        const msg =
          (exec.result?.data as Record<string, unknown>)?.message as string ??
          "Verification failed";
        handleVerificationError("poll_failed", msg);
        return;
      }

      setState("completed");
    } catch (err) {
      logger.error("[WorkflowFaceVerification] Post-iProov flow failed:", err);
      handleVerificationError(
        "submit_failed",
        err instanceof Error ? err.message : "Verification processing failed"
      );
    }
  }, [handleVerificationError]);

  // ── Step 1: Send IDME_READY, listen for IDME_INIT ────────────────────
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      parentOriginRef.current
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[WorkflowFaceVerification] IDME_INIT rejected from origin:", event.origin);
          return;
        }
      } catch {
        logger.warn("[WorkflowFaceVerification] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const raw = event.data.payload as InitPayload;
      const payload: InitPayload = {
        ...raw,
        iproovBaseUrl: normalizeIProovBaseUrl(raw.iproovBaseUrl),
      };
      logger.log("[WorkflowFaceVerification] Received init:", payload.executionId);
      setInitData(payload);
      initDataRef.current = payload;
      // Only from initializing, so a consent failure that lands first isn't overwritten.
      loadIProovWebSdk().then(
        () => setState((s) => (s === "initializing" ? "ready" : s)),
        (err) => handleVerificationError("network_error", err instanceof Error ? err.message : String(err))
      );

      // Fetch biometric consent definition if BFF URL provided.
      // On failure, fire VERIFICATION_ERROR to IPA so it shows an inline error.
      const biometricConsentApiBaseUrl = getBiometricConsentApiBaseUrl(payload.apiBaseUrl);
      if (biometricConsentApiBaseUrl) {
        fetchConsent(biometricConsentApiBaseUrl).catch((err) => {
          logger.error("[WorkflowFaceVerification] Consent definition fetch failed:", err);
          handleVerificationError("consent_get_api_failed", (err as any).apiMessage ?? 'Failed to load biometric consent');
        });
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Step 2: iProov events ─────────────────────────────────────────────
  useWorkflowIProovEvents({
    iproovRef,
    iproovToken: initData?.iproovToken ?? null,
    setState,
    setProgress,
    onVerificationPassed: handleVerificationPassed,
    onVerificationError: handleVerificationError,
  });

  // ── Step 3: Send VERIFICATION_COMPLETE when WE completes ─────────────
  useEffect(() => {
    if (state !== "completed" || !initData) return;
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: {
          executionId: initData.executionId,
          workflowId: initData.workflowId,
          status: "completed",
        },
      },
      parentOriginRef.current
    );
  }, [state, initData]);

  // ── Render — mirrors FaceVerification.tsx exactly ─────────────────────────
  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.contentContainer}>

          {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
          {!fragnoLogoEnabled && (
            <div className={styles.logoContainer}>
              <div className={styles.logo}>
                <img src="/assets/svg/idme-logo.svg" alt={t('face.logo.alt')} />
              </div>
            </div>
          )}

          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner}></div>
            </div>
          )}

          {(state === "ready" || state === "capturing") && initData && (
            <>
              <div className={styles.card}>
                {progress.message && <p className={styles.progress}>{progress.message}</p>}
                <div className={styles.iproovContainer}>
                  <iproov-me
                    {...({ ref: iproovRef } as any)}
                    token={initData.iproovToken}
                    base_url={initData.iproovBaseUrl}
                    logo={fragnoLogoEnabled ? "/assets/svg/idme-logo-rebrand-dark.svg" : "/assets/svg/idme-logo.svg"}
                    language={getIProovLanguage(i18n.language)}
                    filter="vibrant"
                    network_timeout={60}
                  >
                    <WorkflowIProovSlots
                      consentEnabled={!!getBiometricConsentApiBaseUrl(initData?.apiBaseUrl)}
                      biometricConsentApiBaseUrl={getBiometricConsentApiBaseUrl(initData?.apiBaseUrl)}
                      onAcceptConsent={getBiometricConsentApiBaseUrl(initData?.apiBaseUrl) ? handleAcceptBiometricConsent : undefined}
                      onConsentFailed={getBiometricConsentApiBaseUrl(initData?.apiBaseUrl) ? handleBiometricConsentFailed : undefined}
                      onConsentGetFailed={handleBiometricConsentGetFailed}
                    />
                  </iproov-me>
                </div>
              </div>
              <Footer />
            </>
          )}

          {state === "processing" && (
            <div className={styles.card}>
              <h2 className={styles.title}>{t('face.processing.title')}</h2>
              <div className={styles.spinner}></div>
            </div>
          )}

          {state === "completed" && (
            <div className={styles.card}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t('face.completed.title')}</h2>
              <p className={styles.subtitle}>{t('face.completed.subtitle')}</p>
            </div>
          )}

          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t('face.verificationFailed.title')}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
